import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Switch, Button, message, Space, Row, Col, Divider } from 'antd';
import { EditOutlined, TagsOutlined, FileTextOutlined, CloseOutlined, BgColorsOutlined, SaveOutlined } from '@ant-design/icons';
import { tagsApi } from '../../api/tags/tags.api';
import { Tag, UpdateTagRequest } from '../../common/tag.interface';
import moment from 'moment';

interface CModalEditTagProps {
  visible: boolean;
  tag: Tag | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const CModalEditTag: React.FC<CModalEditTagProps> = ({
  visible,
  tag,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tag && visible) {
      form.setFieldsValue({
        name: tag.name,
        description: tag.description || '',
        color: tag.color || '#1890ff',
        isActive: tag.isActive,
      });
    }
  }, [tag, visible, form]);

  const handleSubmit = async (values: any) => {
    if (!tag) return;

    try {
      setLoading(true);
      
      const updateData: UpdateTagRequest = {
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        color: values.color || undefined,
        isActive: values.isActive,
      };

      await tagsApi.updateTag(tag.id, updateData);
      onSuccess();
    } catch (error: any) {
      if (error.response?.status === 409) {
        message.error('Tag với tên này đã tồn tại');
      } else if (error.response?.status === 400) {
        message.error('Dữ liệu không hợp lệ');
      } else if (error.response?.status === 404) {
        message.error('Không tìm thấy tag');
      } else {
        message.error('Không thể cập nhật tag');
      }
      console.error('Error updating tag:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  if (!tag) return null;

  return (
    <Modal
      title={
        <Space>
          <EditOutlined />
          <span>Chỉnh Sửa Tag</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      className="tags-modal"
      destroyOnClose
      maskClosable={true}
    >
      <div className="modal-content">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="tag-form"
        >
          <Row gutter={[16, 12]}>
            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <TagsOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Tên Tag</span>
                  </Space>
                }
                name="name"
                rules={[
                  { required: true, message: 'Vui lòng nhập tên tag' },
                  { min: 2, message: 'Tên tag phải có ít nhất 2 ký tự' },
                  { max: 50, message: 'Tên tag không được vượt quá 50 ký tự' },
                ]}
              >
                <Input 
                  placeholder="Nhập tên tag"
                  className="form-input"
                  maxLength={50}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={
                  <Space>
                    <BgColorsOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Màu sắc</span>
                  </Space>
                }
                name="color"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Input
                    type="color"
                    style={{ width: '50px', padding: '4px', border: '1px solid #d9d9d9', borderRadius: '6px' }}
                    className="form-input"
                  />
                  <Input
                    placeholder="#1890ff"
                    style={{ flex: 1 }}
                    maxLength={7}
                    className="form-input"
                  />
                </div>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={
                  <Space>
                    <FileTextOutlined style={{ fontSize: '12px', color: 'var(--primary-color)' }} />
                    <span>Mô tả</span>
                  </Space>
                }
                name="description"
                rules={[
                  { max: 200, message: 'Mô tả không được vượt quá 200 ký tự' },
                ]}
              >
                <Input.TextArea
                  placeholder="Nhập mô tả cho tag (tùy chọn)"
                  rows={3}
                  maxLength={200}
                  className="form-input"
                />
              </Form.Item>
            </Col>



            <Col span={24}>
              <Form.Item
                label="Trạng thái"
                name="isActive"
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren="Hoạt động" 
                  unCheckedChildren="Vô hiệu hóa"
                />
              </Form.Item>
            </Col>
          </Row>

          <div className="tag-info" style={{
            background: '#f8f9fa',
            padding: '16px',
            borderRadius: '6px',
            margin: '16px 0',
            border: '1px solid #e9ecef'
          }}>
            <Row gutter={[16, 8]}>
              <Col span={8}>
                <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>
                  <strong style={{ color: '#333', marginRight: '8px' }}>ID:</strong>
                  {tag.id}
                </p>
              </Col>
              <Col span={8}>
                <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>
                  <strong style={{ color: '#333', marginRight: '8px' }}>Ngày tạo:</strong>
                  {moment(tag.createdAt).format('DD/MM/YYYY HH:mm')}
                </p>
              </Col>
              <Col span={8}>
                <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>
                  <strong style={{ color: '#333', marginRight: '8px' }}>Cập nhật:</strong>
                  {moment(tag.updatedAt).format('DD/MM/YYYY HH:mm')}
                </p>
              </Col>
            </Row>
          </div>

          <Divider style={{ margin: '16px 0' }} />

          <div className="modal-actions">
            <Button
              onClick={handleCancel}
              className="cancel-button"
              icon={<CloseOutlined />}
            >
              Hủy
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="save-button"
              icon={<SaveOutlined />}
            >
              Cập Nhật
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default CModalEditTag;
